import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import api from "../../../services/api";
import { ChartConfigHelper } from "../../../feature-module/mainMenu/adminDashboard/helpers/chartConfigHelper";
import { getFullName } from "../../../feature-module/ers/reports-settings/report-type/helpers/form.handler";

export interface ComponentItem {
  id: string;
  module: string;
  configKey: string;
  title: string;
  type: "metric" | "chart";
  position: number;
  valueFormat?: string | any;
}

export interface Company {
  id: string;
  name: string;
  type: string;
}

export interface SelectedCompany {
  company_id: string;
  company_name: string;
  company_type: string;
}

export interface DashboardConfiguration {
  id: string | number;
  company_id: string;
  company_type: string;
  name: string;
  user_id: string;
  dashboard_config: {
    topMetrics: ComponentItem[];
    cards: ComponentItem[];
  };
  is_default: boolean;
  status: number;
}

interface ModuleData<T = any> {
  data: T;
  lastUpdated: number;
  filtersHash: string;
}

interface DashboardState {
  isEditMode: boolean;
  topMetrics: ComponentItem[];
  cards: ComponentItem[];
  drafts: {
    topMetrics: ComponentItem[];
    cards: ComponentItem[];
  };
  backendData: {
    assets: ModuleData | null;
    calls: ModuleData | null;
    leads: ModuleData | null;
    deals: ModuleData | null;
    attendance: ModuleData | null;
    company_lead_settings: any;
  };
  companies: Company[];
  companiesLastLoaded: number | null;
  selectedCompany: SelectedCompany | null;
  dashboardConfigurations: DashboardConfiguration[];
  configurationsLastLoaded: number | null;
  configurationsCompanyId: string | null;
  configurationsCompanyType: string | null;
  selectedConfiguration: DashboardConfiguration | null;
  isLoadingCompanies: boolean;
  isLoadingConfigurations: boolean;
  employees: any;
  employeesLastLoaded: Record<string, number>;
  departments: any;
  departmentsLastLoaded: number | null;
  isLoading: boolean;
  isLoadingData: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  isInitializing: boolean;
  globalFilters?: {
    date_range?: {
      start_date: string;
      end_date: string;
    };
    employee_id?: string;
    department_id?: string;
  };
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const API_MAP: Record<string, (body: any) => Promise<any>> = {
  assets: (body) => api.post("userService/getAssetsDashboardData", body),
  calls: (body) => api.post("callLogsService/getUserCallStats", body),
  attendance: (body) => api.post("attendanceService/getUserAttendanceStats", body),
  leads: (body) => api.post("leadService/getUserLeadStats", { ...body, type: 1 }),
  deals: (body) => api.post("leadService/getUserDealStats", { ...body, type: 2 }),
};

const transformComponent = (item: any): ComponentItem => {
  const componentDetails = ChartConfigHelper.getComponentById(item.id);
  return {
    id: item.id,
    module: item.module,
    configKey: componentDetails?.key || item.id.replace(`${item.module}-`, ""),
    title: componentDetails?.config.title || `${item.module} ${item.type}`,
    type: item.type as "metric" | "chart",
    position: item.position,
  };
};

const extractErrorMessage = (error: any): string => {
  const errorData = error?.response?.data || error?.data;
  if (Array.isArray(errorData?.message) && errorData?.message[0]?.msg) {
    return errorData.message[0].msg;
  }
  if (typeof errorData?.message === "string" && errorData.message.trim()) {
    return errorData.message;
  }
  return error.message || "Operation failed";
};

export const loadCompanies = createAsyncThunk(
  "dashboard/loadCompanies",
  async (
    { forceRefresh = false }: { forceRefresh?: boolean } = {},
    { getState, rejectWithValue }
  ) => {
    try {
      const state = getState() as { dashboard: DashboardState };
      const { companies, companiesLastLoaded } = state.dashboard;

      // Check if cache is valid
      const isCacheValid =
        companies.length > 0 &&
        !forceRefresh &&
        companiesLastLoaded &&
        Date.now() - companiesLastLoaded < CACHE_DURATION;

      if (isCacheValid) {
        return { companies, fromCache: true };
      }

      const signature = localStorage.getItem("signature");
      const response = await api.post("userService/getAllCompanyList", {
        signature,
      });

      const companiesData = response?.data?.data?.data || [];
      return {
        companies: companiesData.map((company: any) => ({
          id: company.id.toString(),
          name: company.name,
          type: company.type,
        })),
        fromCache: false,
      };
    } catch (error: any) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const loadEmployees = createAsyncThunk(
  "dashboard/loadEmployees",
  async (
    {
      companyInfo,
      forceRefresh = false,
    }: {
      companyInfo: SelectedCompany;
      forceRefresh?: boolean;
    },
    { getState, rejectWithValue }
  ) => {
    try {
      const state = getState() as { dashboard: DashboardState };
      const { employees, employeesLastLoaded } = state.dashboard;

      const cacheKey = companyInfo.company_id;
      const lastLoaded = employeesLastLoaded[cacheKey];

      // Check if cache is valid for this company
      const isCacheValid =
        employees.length > 0 &&
        !forceRefresh &&
        lastLoaded &&
        Date.now() - lastLoaded < CACHE_DURATION;

      if (isCacheValid) {
        return { employees, companyId: cacheKey, fromCache: true };
      }

      const signature = localStorage.getItem("signature");
      const response = await api.post(
        "userService/getAllEmployeesListDataByCompanyId",
        {
          signature,
          companyId: companyInfo.company_id,
          companyType: companyInfo.company_type,
        }
      );

      const employeesData = response?.data?.data?.data?.allUsers || [];
      return { employees: employeesData, companyId: cacheKey, fromCache: false };
    } catch (error: any) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const loadDepartments = createAsyncThunk(
  "dashboard/loadDepartments",
  async (
    {
      companyInfo,
      forceRefresh = false,
    }: {
      companyInfo: SelectedCompany;
      forceRefresh?: boolean;
    },
    { getState, rejectWithValue }
  ) => {
    try {
      const state = getState() as { dashboard: DashboardState };
      const { departments, departmentsLastLoaded } = state.dashboard;

      // Check if cache is valid
      const isCacheValid =
        departments.length > 0 &&
        !forceRefresh &&
        departmentsLastLoaded &&
        Date.now() - departmentsLastLoaded < CACHE_DURATION;

      if (isCacheValid) {
        return { departments, fromCache: true };
      }

      const signature = localStorage.getItem("signature");
      const response = await api.post("userService/getDepartment", {
        signature,
      });
      const departmentsData = response?.data?.data?.data || [];
      const activeDepartments = departmentsData?.filter(
        (dept: any) => dept.Status == "Active"
      );
      return {
        departments: activeDepartments.map((department: any) => ({
          id: department?.key?.toString(),
          name: department?.Department,
          status: department?.Status,
        })),
        fromCache: false,
      };
    } catch (error: any) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const loadDashboardConfigurations = createAsyncThunk(
  "dashboard/loadDashboardConfigurations",
  async (
    {
      companyInfo,
      forceRefresh = false,
    }: {
      companyInfo?: SelectedCompany;
      forceRefresh?: boolean;
    } = {},
    { getState, rejectWithValue }
  ) => {
    try {
      const state = getState() as { dashboard: DashboardState };
      const { dashboardConfigurations, configurationsLastLoaded, configurationsCompanyId, configurationsCompanyType } = state.dashboard;

      const currentCompanyId = companyInfo?.company_id || null;
      const currentCompanyType = companyInfo?.company_type || null;

      // Check if cache is valid (same company and within time limit)
      const isCacheValid =
        dashboardConfigurations.length > 0 &&
        !forceRefresh &&
        configurationsLastLoaded &&
        configurationsCompanyId === currentCompanyId &&
        configurationsCompanyType === currentCompanyType &&
        Date.now() - configurationsLastLoaded < CACHE_DURATION;

      if (isCacheValid) {
        return { configurations: dashboardConfigurations, companyId: currentCompanyId, companyType: currentCompanyType, fromCache: true };
      }
      const signature = localStorage.getItem("signature");
      const requestBody: any = { signature };

      if (companyInfo) {
        requestBody.company_id = parseInt(companyInfo.company_id);
        requestBody.company_type = companyInfo.company_type;
      }

      const response = await api.post(
        "userService/getUserDashboardConfig",
        requestBody
      );

      const configurations = response?.data?.data?.data || [];

      return {
        configurations: configurations.map((config: any) => ({
          id: config.id.toString(),
          company_id: config.company_id.toString(),
          company_type: config.company_type,
          name: config.name,
          user_id: config.user_id.toString(),
          dashboard_config: {
            topMetrics: (config.dashboard_config?.topMetrics || []).map(
              transformComponent
            ),
            cards: (config.dashboard_config?.cards || []).map(transformComponent),
          },
          is_default: config.is_default,
          status: config.status,
        })),
        companyId: currentCompanyId,
        companyType: currentCompanyType,
        fromCache: false,
      };
    } catch (error: any) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const loadDashboardData = createAsyncThunk(
  "dashboard/loadDashboardData",
  async (
    {
      companyInfo,
      filters,
      forceRefresh = false,
    }: {
      companyInfo?: SelectedCompany;
      filters?: any;
      forceRefresh?: boolean;
    } = {},
    { getState, rejectWithValue }
  ) => {
    try {
      const state = getState() as { dashboard: DashboardState };
      const { backendData, selectedConfiguration, dashboardConfigurations } = state.dashboard;

      const activeConfig = selectedConfiguration ||
        dashboardConfigurations?.find((c: any) => c.is_default) ||
        dashboardConfigurations?.[0];

      const topMetrics = activeConfig?.dashboard_config?.topMetrics || [];
      const cards = activeConfig?.dashboard_config?.cards || [];

      // 1. Identify which modules are actually visible
      const activeModules = new Set<string>();

      // Add 'settings' by default if you always need them
      activeModules.add("company_lead_settings");

      // Scan configured widgets
      [...topMetrics, ...cards].forEach((item) => {
        if (item.module) activeModules.add(item.module);
      });

      // 2. Prepare Request Body
      const signature = localStorage.getItem("signature");
      const requestBody: any = { signature };

      if (companyInfo) {
        requestBody.company_id = parseInt(companyInfo.company_id);
        requestBody.company_type = companyInfo.company_type;
      }
      if (filters) requestBody.filters = filters;

      // Generate a hash of the current filters (simple stringify for comparison)
      const currentFiltersHash = JSON.stringify({
        cId: requestBody.company_id,
        cType: requestBody.company_type,
        filters: filters,
      });

      // 3. Filter: Decide which APIs specifically need to be called
      const promises: Promise<any>[] = [];
      const modulesToFetch = Array.from(activeModules);

      modulesToFetch.forEach((moduleKey) => {
        const cachedModule = backendData[moduleKey as keyof typeof backendData];
        const apiFunction = API_MAP[moduleKey];

        // LOGIC: Skip fetch if...
        // 1. We have data AND
        // 2. We are not forcing a refresh AND
        // 3. The cached data is recent (< 5 mins) AND
        // 4. The filters haven't changed
        const isCacheValid =
          cachedModule?.data &&
          !forceRefresh &&
          Date.now() - (cachedModule.lastUpdated || 0) < CACHE_DURATION &&
          cachedModule.filtersHash === currentFiltersHash;

        if (!isCacheValid && apiFunction) {
          // Create a promise that returns the module name with the data
          promises.push(
            apiFunction(requestBody)
              .then((response) => ({
                status: "fulfilled",
                module: moduleKey,
                result: response.data,
              }))
              .catch((error) => ({
                status: "rejected",
                module: moduleKey,
                error: error,
              }))
          );
        }
      });

      if (promises.length === 0) {
        return null;
      }

      // 4. Execute only necessary requests in parallel
      const results = await Promise.all(promises);

      // 5. Format results for the reducer
      const payload: Record<string, any> = {
        filtersHash: currentFiltersHash,
        timestamp: Date.now(),
      };

      results.forEach((res: any) => {
        if (res.status === "fulfilled") {
          let extractedData = res.result?.data;

          // Handle specific nested keys if your APIs are inconsistent
          if (res.module === "calls") extractedData = extractedData?.calls || [];
          else if (res.module === "attendance") extractedData = extractedData?.attendance || [];
          else if (res.module === "leads") extractedData = extractedData?.leads || [];
          else if (res.module === "deals") extractedData = extractedData?.leads || [];
          else if (res.module === "assets") extractedData = extractedData?.assets || [];

          // Fallback if extraction failed but raw data exists (depends on API)
          if (!extractedData && res.result?.data) extractedData = res.result.data;

          payload[res.module] = extractedData;
        }
      });
      return payload;
    } catch (error: any) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const loadCompanyRelatedData = createAsyncThunk(
  "dashboard/loadCompanyRelatedData",
  async (
    { companyInfo, forceRefresh = false }: { companyInfo: SelectedCompany; forceRefresh?: boolean },
    { dispatch, rejectWithValue }
  ) => {
    try {
      // 1. Load Static Data (Parallel)
      const staticDataPromise = Promise.allSettled([
        dispatch(loadEmployees({ companyInfo, forceRefresh })).unwrap(),
        dispatch(loadDepartments({ companyInfo, forceRefresh })).unwrap(),
      ]);

      // 2. Load Configs (MUST be awaited before fetching data)
      await dispatch(loadDashboardConfigurations({ companyInfo, forceRefresh })).unwrap();

      // 3. Load Data (Now it can see the selected config)
      await dispatch(loadDashboardData({ companyInfo, forceRefresh })).unwrap();

      // 4. Ensure static data is done
      await staticDataPromise;

      return {
        success: true,
        errors: [],
      };
    } catch (error: any) {
      // If critical steps fail, we stop
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const saveDashboardConfig = createAsyncThunk(
  "dashboard/saveDashboardConfig",
  async (
    config: {
      topMetrics: ComponentItem[];
      cards: ComponentItem[];
      name: string;
      is_default: boolean;
      companyInfo?: SelectedCompany;
    },
    { rejectWithValue }
  ) => {
    try {
      const signature = localStorage.getItem("signature");
      const requestBody: any = {
        signature,
        name: config.name,
        dashboard_config: {
          topMetrics: config.topMetrics,
          cards: config.cards,
        },
        is_default: config.is_default,
      };

      if (config.companyInfo) {
        requestBody.company_id = parseInt(config.companyInfo.company_id);
        requestBody.company_type = config.companyInfo.company_type;
      }

      const response = await api.post(
        "userService/addUserDashboardConfig",
        requestBody
      );

      const newConfig = response?.data?.message?.[0]?.data;

      return {
        id: newConfig.id.toString(),
        company_id: newConfig.company_id.toString(),
        company_type: newConfig.company_type,
        name: newConfig.name,
        user_id: newConfig.user_id.toString(),
        dashboard_config: {
          topMetrics: config.topMetrics,
          cards: config.cards,
        },
        is_default: newConfig.is_default,
        status: newConfig.status,
      };
    } catch (error: any) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const updateDashboardConfiguration = createAsyncThunk(
  "dashboard/updateDashboardConfiguration",
  async (
    config: {
      id: string | number;
      name: string;
      dashboard_config: { topMetrics: ComponentItem[]; cards: ComponentItem[] };
      is_default: boolean;
      companyInfo: SelectedCompany;
    },
    { rejectWithValue }
  ) => {
    try {
      const signature = localStorage.getItem("signature");
      const requestBody: any = {
        signature,
        id: parseInt(config.id.toString()),
        name: config.name,
        dashboard_config: config.dashboard_config,
        is_default: config.is_default,
        company_id: parseInt(config.companyInfo.company_id),
        company_type: config.companyInfo.company_type,
      };

      const response = await api.post(
        "userService/updateUserDashboardConfig",
        requestBody
      );

      const updatedConfig = response?.data?.message?.[0]?.data;

      return {
        id: updatedConfig.id.toString(),
        company_id: updatedConfig.company_id.toString(),
        company_type: updatedConfig.company_type,
        name: updatedConfig.name,
        user_id: updatedConfig.user_id.toString(),
        dashboard_config: {
          topMetrics: (updatedConfig.dashboard_config?.topMetrics || []).map(
            transformComponent
          ),
          cards: (updatedConfig.dashboard_config?.cards || []).map(
            transformComponent
          ),
        },
        is_default: updatedConfig.is_default,
        status: updatedConfig.status,
      };
    } catch (error: any) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const deleteDashboardConfiguration = createAsyncThunk(
  "dashboard/deleteDashboardConfiguration",
  async (
    {
      id,
      companyInfo,
    }: {
      id: string | number;
      companyInfo: SelectedCompany;
    },
    { rejectWithValue }
  ) => {
    try {
      const signature = localStorage.getItem("signature");
      await api.post("userService/deleteUserDashboardConfig", {
        signature,
        id: parseInt(id.toString()),
        company_id: parseInt(companyInfo.company_id),
        company_type: companyInfo.company_type,
      });

      return id.toString();
    } catch (error: any) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

const EMPTY_COMPANY: SelectedCompany = {
  company_id: "",
  company_name: "Select Company",
  company_type: "Type",
};

const initialState: DashboardState = {
  isEditMode: false,
  topMetrics: [],
  cards: [],
  drafts: { topMetrics: [], cards: [] },
  backendData: {
    assets: null,
    calls: null,
    leads: null,
    deals: null,
    attendance: null,
    company_lead_settings: null,
  },
  companies: [],
  companiesLastLoaded: null,
  selectedCompany: EMPTY_COMPANY,
  dashboardConfigurations: [],
  configurationsLastLoaded: null,
  configurationsCompanyId: null,
  configurationsCompanyType: null,
  selectedConfiguration: null,
  isLoadingCompanies: false,
  isLoadingConfigurations: false,
  employees: [],
  employeesLastLoaded: {},
  departments: [],
  departmentsLastLoaded: null,
  isLoading: false,
  isLoadingData: false,
  isSaving: false,
  isDeleting: false,
  isInitializing: false,
  globalFilters: {},
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    setSelectedCompany: (state, action: PayloadAction<SelectedCompany>) => {
      const isCompanyChanging =
        state.selectedCompany?.company_id !== action.payload.company_id ||
        state.selectedCompany?.company_type !== action.payload.company_type;

      if (isCompanyChanging) {
        state.backendData = {
          assets: null,
          calls: null,
          leads: null,
          deals: null,
          attendance: null,
          company_lead_settings: null,
        };
        state.dashboardConfigurations = [];
        state.selectedConfiguration = null;
        state.topMetrics = [];
        state.cards = [];
        state.employees = [];
        state.departments = [];
        state.globalFilters = {};
      }
      state.selectedCompany = action.payload;
    },

    clearSelectedCompany: (state) => {
      state.selectedCompany = null;
      state.employees = [];
      state.departments = [];
      state.backendData = {
        assets: null,
        calls: null,
        leads: null,
        deals: null,
        attendance: null,
        company_lead_settings: null,
      };
      state.dashboardConfigurations = [];
      state.selectedConfiguration = null;
      state.topMetrics = [];
      state.cards = [];
    },

    setSelectedConfiguration: (
      state,
      action: PayloadAction<DashboardConfiguration>
    ) => {
      state.selectedConfiguration = action.payload;
      state.topMetrics = action.payload.dashboard_config.topMetrics;
      state.cards = action.payload.dashboard_config.cards;
    },

    clearSelectedConfiguration: (state) => {
      state.selectedConfiguration = null;
    },

    startEditing: (state) => {
      state.isEditMode = true;
      state.drafts.topMetrics = [...state.topMetrics];
      state.drafts.cards = [...state.cards];
    },

    saveChanges: (state) => {
      state.isEditMode = false;
    },

    cancelChanges: (state) => {
      state.isEditMode = false;
      state.drafts = { topMetrics: [], cards: [] };
    },

    reorderComponents: (
      state,
      action: PayloadAction<{
        section: "topMetrics" | "cards";
        items: ComponentItem[];
      }>
    ) => {
      const { section, items } = action.payload;
      const reorderedItems = items.map((item, index) => ({
        ...item,
        position: index,
      }));

      if (state.isEditMode) {
        state.drafts[section] = reorderedItems;
      } else {
        state[section] = reorderedItems;
      }
    },

    addComponent: (
      state,
      action: PayloadAction<{
        section: "topMetrics" | "cards";
        module: string;
        configKey: string;
        componentType: "metric" | "chart";
        id: string;
      }>
    ) => {
      const { section, module, configKey, componentType, id } = action.payload;

      const config =
        componentType === "metric"
          ? ChartConfigHelper.getMetricConfig(module, configKey)
          : ChartConfigHelper.getChartConfig(module, configKey);

      if (!config) {
        return;
      }

      const currentTopMetrics = state.isEditMode
        ? state.drafts.topMetrics
        : state.topMetrics;
      const currentCards = state.isEditMode ? state.drafts.cards : state.cards;
      const allCurrentItems = [...currentTopMetrics, ...currentCards];

      if (
        allCurrentItems.some(
          (item) => item.module === module && item.configKey === configKey
        )
      ) {
        return;
      }

      const targetSection = state.isEditMode
        ? state.drafts[section]
        : state[section];
      const newComponent: ComponentItem = {
        id,
        module,
        configKey,
        title: config.title,
        type: componentType,
        position: targetSection.length,
      };

      targetSection.push(newComponent);
    },

    removeComponent: (
      state,
      action: PayloadAction<{ section: "topMetrics" | "cards"; id: string }>
    ) => {
      const { section, id } = action.payload;
      const targetSection = state.isEditMode
        ? state.drafts[section]
        : state[section];
      const updatedSection = targetSection
        .filter((item) => item.id !== id)
        .map((item, index) => ({ ...item, position: index }));

      if (state.isEditMode) {
        state.drafts[section] = updatedSection;
      } else {
        state[section] = updatedSection;
      }
    },

    resetDashboard: (state) => {
      Object.assign(state, {
        ...initialState,
        backendData: state.backendData,
        companies: state.companies,
        companiesLastLoaded: state.companiesLastLoaded,
        selectedCompany: state.selectedCompany,
        employees: state.employees,
        employeesLastLoaded: state.employeesLastLoaded,
        departments: state.departments,
        departmentsLastLoaded: state.departmentsLastLoaded,
      });
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setGlobalFilters: (state, action: PayloadAction<any>) => {
      state.globalFilters = action.payload;
    },

    clearGlobalFilters: (state) => {
      state.globalFilters = {};
    },

    updateGlobalFilter: (
      state,
      action: PayloadAction<{ key: string; value: any }>
    ) => {
      state.globalFilters = {
        ...state.globalFilters,
        [action.payload.key]: action.payload.value,
      };
    },

    // Action to clear all cache
    clearAllCache: (state) => {
      state.companiesLastLoaded = null;
      state.configurationsLastLoaded = null;
      state.configurationsCompanyId = null;
      state.configurationsCompanyType = null;
      state.employeesLastLoaded = {};
      state.departmentsLastLoaded = null;
      state.backendData = {
        assets: null,
        calls: null,
        leads: null,
        deals: null,
        attendance: null,
        company_lead_settings: null,
      };
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(loadCompanies.pending, (state) => {
        state.isInitializing = true;
        state.isLoadingCompanies = true;
      })
      .addCase(loadCompanies.fulfilled, (state, action) => {
        state.isInitializing = false;
        state.isLoadingCompanies = false;

        // Only update if not from cache
        if (!action.payload.fromCache) {
          state.companies = action.payload.companies;
          state.companiesLastLoaded = Date.now();

          if (!state.selectedCompany?.company_id && action.payload.companies.length > 0) {
            const firstCompany = action.payload.companies[0];
            state.selectedCompany = {
              company_id: firstCompany.id,
              company_name: firstCompany.name,
              company_type: firstCompany.type,
            };
          }
        }
      })
      .addCase(loadCompanies.rejected, (state) => {
        state.isInitializing = false;
        state.isLoadingCompanies = false;
      })

      .addCase(loadCompanyRelatedData.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(loadCompanyRelatedData.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(loadCompanyRelatedData.rejected, (state) => {
        state.isLoading = false;
      })

      .addCase(loadEmployees.fulfilled, (state, action) => {
        if (!action.payload.fromCache) {
          state.employees = action.payload.employees;
          state.employeesLastLoaded[action.payload.companyId] = Date.now();
        }
      })
      .addCase(loadEmployees.rejected, (state) => {
        state.employees = [];
      })

      .addCase(loadDepartments.fulfilled, (state, action) => {
        if (!action.payload.fromCache) {
          state.departments = action.payload.departments;
          state.departmentsLastLoaded = Date.now();
        }
      })
      .addCase(loadDepartments.rejected, (state) => {
        state.departments = [];
      })

      .addCase(loadDashboardConfigurations.pending, (state) => {
        state.isLoadingConfigurations = true;
      })
      .addCase(loadDashboardConfigurations.fulfilled, (state, action) => {
        state.isLoadingConfigurations = false;

        if (!action.payload.fromCache) {
          state.dashboardConfigurations = action.payload.configurations;
          state.configurationsLastLoaded = Date.now();
          state.configurationsCompanyId = action.payload.companyId;
          state.configurationsCompanyType = action.payload.companyType;
        }


        if (action.payload.configurations.length > 0) {
          const defaultConfig = action.payload.configurations.find(
            (config: DashboardConfiguration) => config.is_default === true
          );
          const selectedConfig = defaultConfig || action.payload.configurations[0];

          if (!state.selectedConfiguration || state.selectedConfiguration.id !== selectedConfig.id) {
            state.selectedConfiguration = selectedConfig;
          }
          state.topMetrics = selectedConfig.dashboard_config.topMetrics;
          state.cards = selectedConfig.dashboard_config.cards;
        } else {
          state.selectedConfiguration = null;
          state.topMetrics = [];
          state.cards = [];
        }
      })
      .addCase(loadDashboardConfigurations.rejected, (state) => {
        state.isLoadingConfigurations = false;
        state.dashboardConfigurations = [];
      })

      .addCase(saveDashboardConfig.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(saveDashboardConfig.fulfilled, (state, action) => {
        state.isSaving = false;

        state.dashboardConfigurations.push(action.payload);

        if (action.payload.is_default) {
          state.dashboardConfigurations = state.dashboardConfigurations.map(
            (config) =>
              config.id === action.payload.id
                ? config
                : { ...config, is_default: false }
          );
        }

        state.selectedConfiguration = action.payload;
        state.topMetrics = action.payload.dashboard_config.topMetrics;
        state.cards = action.payload.dashboard_config.cards;
        state.drafts = { topMetrics: [], cards: [] };

        // Invalidate configurations cache since we added new config
        state.configurationsLastLoaded = Date.now();
      })
      .addCase(saveDashboardConfig.rejected, (state) => {
        state.isSaving = false;
        state.isEditMode = true;
      })

      .addCase(updateDashboardConfiguration.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(updateDashboardConfiguration.fulfilled, (state, action) => {
        state.isSaving = false;

        const index = state.dashboardConfigurations.findIndex(
          (config) => config.id === action.payload.id
        );

        if (index !== -1) {
          state.dashboardConfigurations[index] = action.payload;

          if (action.payload.is_default) {
            state.dashboardConfigurations = state.dashboardConfigurations.map(
              (config) =>
                config.id === action.payload.id
                  ? config
                  : { ...config, is_default: false }
            );
          }

          if (state.selectedConfiguration?.id === action.payload.id) {
            state.selectedConfiguration = action.payload;
            state.topMetrics = action.payload.dashboard_config.topMetrics;
            state.cards = action.payload.dashboard_config.cards;
          }
        }

        state.drafts = { topMetrics: [], cards: [] };

        // Update cache timestamp
        state.configurationsLastLoaded = Date.now();
      })
      .addCase(updateDashboardConfiguration.rejected, (state) => {
        state.isSaving = false;
      })

      .addCase(deleteDashboardConfiguration.pending, (state) => {
        state.isDeleting = true;
      })
      .addCase(deleteDashboardConfiguration.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.dashboardConfigurations = state.dashboardConfigurations.filter(
          (config) => config.id !== action.payload
        );

        if (state.selectedConfiguration?.id === action.payload) {
          const defaultConfig = state.dashboardConfigurations.find(
            (config) => config.is_default
          );
          const newSelected = defaultConfig || state.dashboardConfigurations[0];

          if (newSelected) {
            state.selectedConfiguration = newSelected;
            state.topMetrics = newSelected.dashboard_config.topMetrics;
            state.cards = newSelected.dashboard_config.cards;
          } else {
            state.selectedConfiguration = null;
            state.topMetrics = [];
            state.cards = [];
          }
        }

        // Update cache timestamp
        state.configurationsLastLoaded = Date.now();
      })
      .addCase(deleteDashboardConfiguration.rejected, (state) => {
        state.isDeleting = false;
      })

      .addCase(loadDashboardData.pending, (state) => {
        state.isLoadingData = true;
      })
      .addCase(loadDashboardData.fulfilled, (state, action) => {
        state.isLoadingData = false;
        if (!action.payload) return;
        const { filtersHash, timestamp, ...newModules } = action.payload;

        if (!state.backendData) {
          state.backendData = {
            assets: null, calls: null, leads: null, deals: null,
            attendance: null, company_lead_settings: null
          };
        }

        Object.keys(newModules).forEach((key) => {
          const moduleKey = key as keyof typeof state.backendData;
          const newData = {
            data: newModules[key],
            lastUpdated: timestamp,
            filtersHash: filtersHash,
          };
          (state.backendData as any)[moduleKey] = newData;
        });
      })
      .addCase(loadDashboardData.rejected, (state) => {
        state.isLoadingData = false;
      });
  },
});

const computeComponentData = (component: ComponentItem, backendData: any) => {
  const config =
    component.type === "metric"
      ? ChartConfigHelper.getMetricConfig(component.module, component.configKey)
      : ChartConfigHelper.getChartConfig(component.module, component.configKey);
  if (
    !config ||
    !backendData[component.module] ||
    !backendData[component.module].data
  ) {
    return null;
  }
  const moduleData = backendData[component.module].data;
  return ChartConfigHelper.extractDataFromResponse(
    { [component.module]: moduleData },
    config.dataPath
  );
};

export const selectTopMetricsWithData = (
  state: { dashboard: DashboardState },
  userRole: string,
  userDepartmentId: number | any
) => {

  const components = state.dashboard.isEditMode
    ? state.dashboard.drafts.topMetrics
    : state.dashboard.topMetrics;

  return components
    .filter((component) =>
      shouldShowComponent(
        component,
        userRole,
        userDepartmentId,
        state.dashboard.backendData
      )
    )
    .map((component) => ({
      ...component,
      data: computeComponentData(component, state.dashboard.backendData),
    }));
};

export const selectCardsWithData = (
  state: { dashboard: DashboardState },
  userRole: string,
  userDepartmentId: number | any
) => {
  const components = state.dashboard.isEditMode
    ? state.dashboard.drafts.cards
    : state.dashboard.cards;

  return components
    .filter((component) =>
      shouldShowComponent(
        component,
        userRole,
        userDepartmentId,
        state.dashboard.backendData
      )
    )
    .map((component) => ({
      ...component,
      data: computeComponentData(component, state.dashboard.backendData),
    }));
};

export const selectCompanies = (state: { dashboard: DashboardState }) =>
  state.dashboard.companies;

export const selectSelectedCompany = (state: { dashboard: DashboardState }) =>
  state.dashboard.selectedCompany;

export const selectIsLoadingCompanies = (state: {
  dashboard: DashboardState;
}) => state.dashboard.isLoadingCompanies;

export const selectCompanyOptions = (state: { dashboard: DashboardState }) =>
  state.dashboard.companies.map((company) => ({
    value: company.id,
    label: `${company.name} - ${company.type}`,
    companyName: company.name,
    companyType: company.type,
  }));

export const selectEmployees = (state: { dashboard: DashboardState }) =>
  state.dashboard.employees;

export const selectDepartments = (state: { dashboard: DashboardState }) =>
  state.dashboard.departments;

export const selectEmployeeOptions = (state: { dashboard: DashboardState }) =>
  state.dashboard.employees.map((user: any) => ({
    label: `${getFullName(user?.salutation, user?.fname, user?.lname)}`,
    value: user?.id?.toString(),
    imageUrl: user?.profile_pic || null,
  }));

export const selectDepartmentOptions = (state: { dashboard: DashboardState }) =>
  state.dashboard.departments.map((department: any) => ({
    value: department?.id?.toString(),
    label: department?.name,
  }));

export const selectDashboardConfigurations = (state: {
  dashboard: DashboardState;
}) => state.dashboard.dashboardConfigurations;

export const selectSelectedConfiguration = (state: {
  dashboard: DashboardState;
}) => state.dashboard.selectedConfiguration;

export const selectIsLoadingConfigurations = (state: {
  dashboard: DashboardState;
}) => state.dashboard.isLoadingConfigurations;

export const selectConfigurationOptions = (state: {
  dashboard: DashboardState;
}) =>
  state.dashboard.dashboardConfigurations.map((config) => ({
    value: config.id?.toString(),
    label: config.name,
    isDefault: config.is_default,
  }));

export const selectIsEditMode = (state: { dashboard: DashboardState }) =>
  state.dashboard.isEditMode;

export const selectIsLoading = (state: { dashboard: DashboardState }) =>
  state.dashboard.isLoading;

export const selectIsLoadingData = (state: { dashboard: DashboardState }) =>
  state.dashboard.isLoadingData;

export const selectIsInitializing = (state: { dashboard: DashboardState }) =>
  state.dashboard.isInitializing;

export const selectIsSaving = (state: { dashboard: DashboardState }) =>
  state.dashboard.isSaving;

export const selectIsDeleting = (state: { dashboard: DashboardState }) =>
  state.dashboard.isDeleting;

export const selectBackendData = (state: { dashboard: DashboardState }) =>
  state.dashboard.backendData;

export const selectCurrentTopMetrics = (
  state: { dashboard: DashboardState },
  userRole: string,
  userDepartmentId: number | any
) => {
  const components = state.dashboard.isEditMode
    ? state.dashboard.drafts.topMetrics
    : state.dashboard.topMetrics;

  return components.filter((component) =>
    shouldShowComponent(
      component,
      userRole,
      userDepartmentId,
      state.dashboard.backendData
    )
  );
};

export const selectCurrentCards = (
  state: { dashboard: DashboardState },
  userRole: string,
  userDepartmentId: number | any
) => {
  const components = state.dashboard.isEditMode
    ? state.dashboard.drafts.cards
    : state.dashboard.cards;

  return components.filter((component) =>
    shouldShowComponent(
      component,
      userRole,
      userDepartmentId,
      state.dashboard.backendData
    )
  );
};

export const selectAvailableTemplates = (
  state: { dashboard: DashboardState },
  userRole: string,
  userDepartmentId: number | any
) => {
  const leadSettings = state.dashboard?.backendData?.company_lead_settings;
  const isEmployee = userRole?.toLowerCase() === "employee";

  const modules = ChartConfigHelper.getAvailableModules();
  const templates = {
    metrics: {} as Record<string, any[]>,
    charts: {} as Record<string, any[]>,
  };

  modules.forEach((module) => {
    const moduleMetrics = ChartConfigHelper.getModuleMetrics(module);
    const moduleCharts = ChartConfigHelper.getModuleCharts(module);

    templates.metrics[module] = Object.entries(moduleMetrics)
      .filter(([key, config]) => {
        if (isEmployee && config?.onlyForAdmin === true) {
          return false;
        }
        if (["leads", "deals", "calls"].includes(module) && isEmployee) {
          if (
            leadSettings?.settings?.lead_assigned_to_department !=
            userDepartmentId
          ) {
            return false;
          }
        }
        return true;
      })
      .map(([key, config]) => ({
        ...config,
        module,
        configKey: key,
        type: "metric",
      }));

    templates.charts[module] = Object.entries(moduleCharts)
      .filter(([key, config]) => {
        if (isEmployee && config?.onlyForAdmin === true) {
          return false;
        }
        if (["leads", "deals", "calls"].includes(module) && isEmployee) {
          if (
            leadSettings?.settings?.lead_assigned_to_department !=
            userDepartmentId
          ) {
            return false;
          }
        }
        return true;
      })
      .map(([key, config]) => ({
        ...config,
        module,
        configKey: key,
        type: "chart",
      }));
  });

  return templates;
};

export const selectUsedComponents = (
  state: { dashboard: DashboardState },
  userRole: string,
  userDepartmentId: number | any
) => {
  const currentTopMetrics = selectCurrentTopMetrics(
    state,
    userRole,
    userDepartmentId
  );
  const currentCards = selectCurrentCards(state, userRole, userDepartmentId);
  return [...currentTopMetrics, ...currentCards].map(
    (item) => `${item.module}.${item.configKey}`
  );
};

export const selectGlobalFilters = (state: { dashboard: DashboardState }) =>
  state.dashboard.globalFilters;

export const { setGlobalFilters, clearGlobalFilters, updateGlobalFilter } =
  dashboardSlice.actions;

export const {
  setSelectedCompany,
  clearSelectedCompany,
  setSelectedConfiguration,
  clearSelectedConfiguration,
  startEditing,
  saveChanges,
  cancelChanges,
  reorderComponents,
  addComponent,
  removeComponent,
  resetDashboard,
  setLoading,
  clearAllCache,
} = dashboardSlice.actions;

const shouldShowComponent = (
  component: ComponentItem,
  userRole: string,
  userDepartmentId: number | any,
  backendData: any
) => {
  const leadSettings = backendData?.company_lead_settings;
  const config =
    component.type === "metric"
      ? ChartConfigHelper.getMetricConfig(component.module, component.configKey)
      : ChartConfigHelper.getChartConfig(component.module, component.configKey);
  if (!config) return false;
  const isEmployee = userRole?.toLowerCase() === "employee";
  if (isEmployee && config?.onlyForAdmin === true) {
    return false;
  }

  if (["leads", "deals", "calls"].includes(component?.module) && isEmployee) {
    if (
      leadSettings?.settings?.lead_assigned_to_department != userDepartmentId
    ) {
      return false;
    }
  }

  return true;
};

export default dashboardSlice.reducer;
